// Blog.jsx — public blog list (Hexaplay Private Theater Booking)
// Shows ONLY blogs published from the Admin Blog Manager.
// No demo/fallback data — if the admin has not posted anything,
// the page shows a clean empty state.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Blog.css";

/* ============================================================
   API CONFIGURATION
============================================================ */

const API = "https://www.thehexaplay.com/api/blogs";
const SITE_NAME = "Hexaplay";

/* Accent colors rotate across the cards */
const COLORS = [
  "#e11d48",
  "#7c3aed",
  "#2563eb",
  "#ea580c",
  "#0891b2",
  "#16a34a",
];

/* Default categories (theater-themed) — used only when the
   admin's blogs don't provide their own categories yet. */
const DEFAULT_CATEGORIES = [
  "Private Theater",
  "Movie Nights",
  "Birthday Parties",
  "Couple Celebrations",
  "Booking Tips",
];

/* Inline SVG placeholder — always renders, no external request */
const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='350'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial, sans-serif' font-size='26' font-weight='700' fill='%2394a3b8' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

export default function Blog() {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([
    "All",
    ...DEFAULT_CATEGORIES,
  ]);
  const [activeCat, setActiveCat] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  /* Always open the blog list at the top of the page */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  /* ============================================================
     SEO — PAGE TITLE + META DESCRIPTION
  ============================================================ */

  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector(
      'meta[name="description"]'
    );
    const previousDescription = metaDescription
      ? metaDescription.getAttribute("content")
      : null;

    document.title = `Private Theater Blog & Insights | ${SITE_NAME}`;

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Planning tips, celebration ideas and booking guides for your private theater movie experience — from the Hexaplay team."
      );
    }

    return () => {
      document.title = previousTitle;

      if (metaDescription && previousDescription !== null) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, []);

  /* ============================================================
     FETCH ONLY ADMIN-PUBLISHED BLOGS
     The page reflects exactly what the admin publishes.
  ============================================================ */

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${API}/published`);

        const list = Array.isArray(response.data?.blogs)
          ? response.data.blogs
          : [];

        const published = list.filter(
          (blog) => (blog.status || "Published") === "Published"
        );

        setBlogs(published);

        /*
         * Build the category filter from the admin's real data,
         * so the buttons always match the published blogs.
         */
        const uniqueCategories = [
          ...new Set(
            published
              .map((blog) => blog.category)
              .filter(Boolean)
          ),
        ];

        if (uniqueCategories.length > 0) {
          setCategories(["All", ...uniqueCategories]);
        } else {
          setCategories(["All", ...DEFAULT_CATEGORIES]);
        }

        /* Reset the filter if the selected category disappeared */
        setActiveCat((current) =>
          current === "All" ||
          uniqueCategories.includes(current)
            ? current
            : "All"
        );
      } catch (err) {
        console.error("Failed to fetch blogs:", err);

        setBlogs([]);
        setCategories(["All", ...DEFAULT_CATEGORIES]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  /* ============================================================
     FILTERING — category + search (title, excerpt and tags)
  ============================================================ */

  const filtered = blogs.filter((blog) => {
    const matchCat =
      activeCat === "All" || blog.category === activeCat;

    const term = search.toLowerCase().trim();

    const matchSearch =
      (blog.title || "").toLowerCase().includes(term) ||
      (blog.excerpt || "").toLowerCase().includes(term) ||
      (blog.tags || []).some((tag) =>
        String(tag).toLowerCase().includes(term)
      );

    return matchCat && matchSearch;
  });

  /* ============================================================
     HELPERS
  ============================================================ */

  const formatDate = (d) => {
    if (!d) return "N/A";

    const date = new Date(d);

    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const openArticle = (slug) => {
    if (!slug) return;
    navigate(`/blog/${slug}`);
  };

  /* If an admin-posted image ever fails to load, swap to the
     inline placeholder instead of showing a broken image. */
  const handleImageError = (event) => {
    if (event.currentTarget.src !== PLACEHOLDER_IMG) {
      event.currentTarget.src = PLACEHOLDER_IMG;
    }
  };

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      <div className="blog-wrapper">
        {/* ============ HERO HEADER ============ */}
        <header className="blog-header">
          <span className="blog-badge">
            🎬 Hexaplay Blog • Private Theater Experiences
          </span>

          <h1>
            Private Theater &amp; Movie Night{" "}
            <span>Insights</span>
          </h1>

          <p>
            Planning tips, celebration ideas and booking guides for
            your own private theater — birthdays, couple movie nights
            and unforgettable screenings, curated by the Hexaplay team.
          </p>

          <div className="blog-controls">
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="blog-search"
              aria-label="Search articles"
            />
          </div>

          <div
            className="blog-categories"
            role="tablist"
            aria-label="Blog categories"
          >
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={activeCat === category ? "active" : ""}
                onClick={() => setActiveCat(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </header>

        {/* ============ LOADING SKELETON ============ */}
        {loading ? (
          <div className="blog-grid" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                className="blog-skeleton"
                key={`skeleton-${index}`}
              />
            ))}
          </div>
        ) : (
          /* ============ BLOG GRID ============ */
          <div className="blog-grid">
            {filtered.map((blog, index) => (
              <article
                key={blog._id || blog.id || blog.slug}
                className="blog-card"
                style={{
                  "--card-accent": COLORS[index % COLORS.length],
                }}
              >
                <div className="blog-card-img-wrap">
                  <img
                    src={blog.featuredImage || PLACEHOLDER_IMG}
                    /* SEO — alt text saved from the Blog Manager */
                    alt={
                      blog.imageAlt ||
                      blog.title ||
                      "Hexaplay private theater blog"
                    }
                    loading="lazy"
                    onError={handleImageError}
                  />

                  <span className="blog-cat-badge">
                    {blog.category || "Private Theater"}
                  </span>
                </div>

                <div className="blog-card-body">
                  <div className="blog-meta-top">
                    <span>📅 {formatDate(blog.pubDate)}</span>

                    <span className="meta-dot">•</span>

                    <span>
                      ✍️ {blog.author || "Hexaplay Team"}
                    </span>

                    {blog.readTime ? (
                      <>
                        <span className="meta-dot">•</span>
                        <span>⏱ {blog.readTime} min read</span>
                      </>
                    ) : null}
                  </div>

                  <h3>{blog.title || "Untitled Article"}</h3>

                  <p className="blog-excerpt">
                    {blog.excerpt || "No description available."}
                  </p>

                  {(blog.tags || []).length > 0 && (
                    <div className="blog-tags-row">
                      {(blog.tags || []).slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="mini-tag"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="blog-readmore"
                    onClick={() => openArticle(blog.slug)}
                  >
                    Read More <span>→</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ============ EMPTY STATE ============ */}
        {!loading && filtered.length === 0 && (
          <div className="blog-empty">
            <span className="blog-empty-icon">🎬</span>

            <p>
              {search || activeCat !== "All"
                ? "No articles match your search. Try another keyword or category."
                : "No stories published yet. Our team is preparing new private theater guides — check back soon!"}
            </p>
          </div>
        )}
      </div>
    </>
  );
}